import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { productAPI, customerAPI } from '../services/api';
import Header from './Header';
import { Country, Customer } from '../shared-types';
import { formatDateTime } from '../utils/dateUtils';
import { useExcelTableFilter } from './useExcelTableFilter';
import { ExcelHeaderCell, ExcelActiveFiltersBar } from './ExcelHeaderCell';

interface ProductsProps {
  user: any;
  onLogout: () => void;
}

interface SkuSuggestion {
  sku_code: string;
  product_name: string;
}

interface ProductFormData {
  sku_code: string;
  product_name: string;
  category: string;
  country_id: number | null;
  customer: string;
  pack_size: string;
  standard_batch_size: string;
  moq: string;
  primary_pm_code: string;
  secondary_pm_code: string;
  leaf_pm_code: string;
  artwork_status: string;
}

const Products: React.FC<ProductsProps> = ({ user, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [pmModal, setPmModal] = useState<{ sku: string; primaryPmCode: string; secondaryPmCode: string; leafPmCode: string } | null>(null);
  const [primaryPmCodeInput, setPrimaryPmCodeInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<string>('all');
  const [viewProductModal, setViewProductModal] = useState<any | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('search') || params.get('query');
    if (q) {
      setSearchTerm(q);
    }
    if (user?.department === 'Artwork') {
      const tabParam = params.get('tab');
      if (tabParam) {
        setActiveTab(tabParam);
      } else {
        setActiveTab('submit_pm_orders');
      }
    } else {
      setActiveTab('all');
    }
  }, [location.search, user?.department]);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    const params = new URLSearchParams(location.search);
    params.set('tab', tab);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  const [skuSuggestions, setSkuSuggestions] = useState<SkuSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Dropdown options from database & defaults
  const [countries, setCountries] = useState<Country[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);

  // PM Code Request workflow states
  const [historyModal, setHistoryModal] = useState<any | null>(null);
  const [rejectModal, setRejectModal] = useState<{
    requestId: number;
    sku?: string;
    primaryPmCode?: string;
    secondaryPmCode?: string;
    leafPmCode?: string;
  } | null>(null);
  const [rejectRemarks, setRejectRemarks] = useState('');
  const [artworkSubmitModal, setArtworkSubmitModal] = useState<{ requestId: number; sku: string } | null>(null);
  const [secondaryPmCodeInput, setSecondaryPmCodeInput] = useState('');
  const [leafPmCodeInput, setLeafPmCodeInput] = useState('');
  const [artworkRemarks, setArtworkRemarks] = useState('');
  const [acceptModal, setAcceptModal] = useState<{
    requestId: number;
    primaryPmCode: string;
    secondaryPmCode: string;
    leafPmCode: string;
    artworkStatus: string;
  } | null>(null);
  const [acceptRemarks, setAcceptRemarks] = useState('');

  const [isEditMode, setIsEditMode] = useState(false);
  const [editProductId, setEditProductId] = useState<number | null>(null); // To store the ID of the product being edited

  const [formData, setFormData] = useState<ProductFormData>({
    sku_code: '',
    product_name: '',
    category: 'PP',
    country_id: null,
    customer: '',
    pack_size: '',
    standard_batch_size: '',
    moq: '',
    primary_pm_code: '',
    secondary_pm_code: '',
    leaf_pm_code: '',
    artwork_status: 'Not Available',
  });

  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);

  const isRegulatory = user?.department === 'Regulatory';

  useEffect(() => {
    fetchProducts(user?.department);
    fetchMasterData();
  }, [user?.department]);

  const fetchMasterData = async () => {
    try {
      const countryRes = await productAPI.getCountries();
      if (countryRes.data) {
        setCountries(countryRes.data);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
    }

    try {
      const custRes = await customerAPI.getCustomers(undefined, undefined, undefined, 0, 1000);
      if (custRes.data) {
        setCustomers(custRes.data);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  useEffect(() => {
    let filtered = customers;
    if (formData.country_id) {
      filtered = filtered.filter(c => c.country?.id === formData.country_id);
    }
    setFilteredCustomers(filtered);
  }, [formData.country_id, customers]);

  const fetchProducts = async (scmUserType?: string) => {
    try {
      setLoading(true);
      const response = await productAPI.getProducts(undefined, undefined, scmUserType);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      sku_code: '',
      product_name: '',
      category: 'PP',
      country_id: null,
      customer: '',
      pack_size: '',
      standard_batch_size: '',
      moq: '',
      primary_pm_code: '',
      secondary_pm_code: '',
      leaf_pm_code: '',
      artwork_status: 'Not Available',
    });
    setIsEditMode(false);
    setEditProductId(null);
  };

  const handleAddProductClick = async () => {
    setShowModal(true);
    resetForm();
    try {
      const res = await productAPI.getLastSku();
      if (res.data && res.data.sku) {
        alert(`Note: The last created SKU code was: ${res.data.sku}`);
      }
    } catch (e) {
      console.error('Error fetching last SKU', e);
    }
  };

  useEffect(() => {
    const checkDuplicate = async () => {
      if (formData.country_id && formData.customer && formData.pack_size) {
        try {
          const res = await productAPI.checkDuplicate(formData.country_id, formData.customer, formData.pack_size);
          if (res.data.is_duplicate) {
            alert(`Duplicate product found with SKU: ${res.data.sku}. You cannot create duplicates.`);
            setFormData(prev => ({ ...prev, pack_size: '' })); // Block duplicate by clearing pack size
          }
        } catch (e) {
          console.error('Error checking duplicate', e);
        }
      }
    };
    if (showModal) {
      checkDuplicate();
    }
  }, [formData.country_id, formData.customer, formData.pack_size, showModal]);

  const handleProductSelect = async (selected: SkuSuggestion) => {
    setFormData({ ...formData, product_name: selected.product_name });
    setShowSuggestions(false);
    setSkuSuggestions([]);

    try {
      const response = await productAPI.getProductBySku(selected.sku_code);
      if (response.data) {
        setFormData({
          ...formData,
          ...response.data,
          category: response.data.category || 'PP',
          standard_batch_size: response.data.standard_batch_size || '',
          moq: response.data.moq || '',
          sku_code: response.data.sku_code,
          country_id: response.data.country?.id || null,
        });
        setIsEditMode(true);
        setEditProductId(response.data.id || null);
      }
    } catch (error) {
      console.error('Error fetching product details:', error);
    }
  };

  const handleProductNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setFormData({ ...formData, product_name: name });

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    if (name.length > 2) {
      searchTimeoutRef.current = setTimeout(async () => {
        try {
          const res = await productAPI.searchProducts(name);
          setSkuSuggestions(res.data.products || []);
          setShowSuggestions(true);
        } catch (e) {
          console.error(e);
        }
      }, 300);
    } else {
      setSkuSuggestions([]);
      setShowSuggestions(false);
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      console.log("Submitting product with PM Code:", formData.primary_pm_code);
      await productAPI.createProduct({
        ...formData,
        standard_batch_size: parseInt(formData.standard_batch_size) || null,
        moq: parseInt(formData.moq) || null,
        primary_pm_code: formData.artwork_status === 'Available' ? formData.primary_pm_code : '',
        secondary_pm_code: formData.artwork_status === 'Available' ? formData.secondary_pm_code : '',
        leaf_pm_code: formData.artwork_status === 'Available' ? formData.leaf_pm_code : '',
        current_artwork_version: '',
      });
      alert('Product created successfully!');
      setShowModal(false);
      fetchProducts();
      resetForm(); // Reset form after submission
    } catch (error) {
      console.error('Error creating product:', error);
      alert('Error creating product');
    }
  };

  const handlePmCodeUpdate = async () => {
    if (!pmModal) return;
    try {
      await productAPI.updatePmCode(pmModal.sku, primaryPmCodeInput, secondaryPmCodeInput, leafPmCodeInput);
      setPmModal(null);
      setPrimaryPmCodeInput('');
      setSecondaryPmCodeInput('');
      setLeafPmCodeInput('');
      alert('PM Code submitted for Regulatory team approval!');
      fetchProducts();
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Error updating PM Code');
    }
  };

  const handleRequestPmCode = async (sku: string) => {
    try {
      await productAPI.requestPmCode(sku);
      fetchProducts();
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Error requesting PM Code');
    }
  };

  const handleDecidePmCode = async (
    requestId: number,
    decision: 'ACCEPT' | 'REJECT',
    remarks?: string,
    primaryPmCode?: string,
    secondaryPmCode?: string,
    leafPmCode?: string,
    artworkStatus?: string
  ) => {
    try {
      await productAPI.decidePmCode(requestId, decision, remarks, primaryPmCode, secondaryPmCode, leafPmCode, artworkStatus);
      fetchProducts();
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Error submitting decision');
    }
  };

  const handleArtworkSubmit = async () => {
    if (!artworkSubmitModal || !primaryPmCodeInput.trim()) return;
    try {
      await productAPI.submitArtworkPmCode(
        artworkSubmitModal.requestId,
        primaryPmCodeInput.trim(),
        secondaryPmCodeInput.trim(),
        leafPmCodeInput.trim(),
        artworkRemarks
      );
      setArtworkSubmitModal(null);
      setPrimaryPmCodeInput('');
      setSecondaryPmCodeInput('');
      setLeafPmCodeInput('');
      setArtworkRemarks('');
      fetchProducts();
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Error submitting PM Code');
    }
  };

  const getArtworkStatusClass = (status: string) => {
    switch (status) {
      case 'Available': return 'status-accepted';
      case 'Pending': return 'status-hold';
      default: return 'status-risk';
    }
  };

  const submitPmOrdersProducts = useMemo(() => {
    return products.filter((product) => {
      const requests = product.pm_code_requests || [];
      const latestRequest = requests[requests.length - 1];
      return latestRequest && latestRequest.status === 'PENDING_ARTWORK';
    });
  }, [products]);

  const updatePmCodesProducts = useMemo(() => {
    return products.filter((product) => {
      const requests = product.pm_code_requests || [];
      const latestRequest = requests[requests.length - 1];
      return !latestRequest || (latestRequest.status !== 'PENDING_ARTWORK' && latestRequest.status !== 'AWAITING_REGULATORY_APPROVAL');
    });
  }, [products]);

  const filteredProducts = useMemo(() => {
    const isArtwork = user?.department === 'Artwork';
    const trimmedSearch = searchTerm.trim().toLowerCase();

    return products.filter((product) => {
      const requests = product.pm_code_requests || [];
      const latestRequest = requests[requests.length - 1];

      if (isArtwork) {
        if (activeTab === 'submit_pm_orders') {
          if (!latestRequest || latestRequest.status !== 'PENDING_ARTWORK') return false;
        } else if (activeTab === 'update_pm_codes') {
          if (latestRequest && (latestRequest.status === 'PENDING_ARTWORK' || latestRequest.status === 'AWAITING_REGULATORY_APPROVAL')) return false;
        }
      }

      if (!trimmedSearch) return true;
      return (
        (product.sku_code && product.sku_code.toLowerCase().includes(trimmedSearch)) ||
        (product.product_name && product.product_name.toLowerCase().includes(trimmedSearch)) ||
        (product.customer && product.customer.toLowerCase().includes(trimmedSearch)) ||
        (product.country?.name && product.country.name.toLowerCase().includes(trimmedSearch)) ||
        (product.pack_size && product.pack_size.toLowerCase().includes(trimmedSearch)) ||
        (product.primary_pm_code && product.primary_pm_code.toLowerCase().includes(trimmedSearch)) ||
        (product.artwork_status && product.artwork_status.toLowerCase().includes(trimmedSearch))
      );
    });
  }, [products, user?.department, activeTab, searchTerm]);

  const columnAccessors = useMemo(() => ({
    sku_code: (p: any) => p.sku_code || '-',
    product_name: (p: any) => p.product_name || '-',
    country: (p: any) => p.country?.name || '-',
    customer: (p: any) => p.customer || '-',
    pack_size: (p: any) => p.pack_size || '-',
    pm_code: (p: any) => p.primary_pm_code || '-',
    artwork_status: (p: any) => p.artwork_status || '-',
    pm_request_status: (p: any) => {
      const requests = p.pm_code_requests || [];
      const latestRequest = requests[requests.length - 1];
      if (!latestRequest) return '—';
      switch (latestRequest.status) {
        case 'PENDING_ARTWORK': return 'Awaiting Artwork PM Code';
        case 'AWAITING_REGULATORY_APPROVAL': return 'Awaiting Regulatory Approval';
        case 'APPROVED': return 'Approved';
        case 'REJECTED': return 'Rejected';
        default: return latestRequest.status;
      }
    },
  }), []);

  const {
    filteredAndSortedData: excelFilteredProducts,
    filterState,
    sortState,
    uniqueValuesMap,
    setColumnFilter,
    handleSort,
    clearColumnFilter,
    clearAllFilters,
    activeFilterCount,
  } = useExcelTableFilter(filteredProducts, columnAccessors);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, activeTab, filterState]);

  const totalPages = Math.max(1, Math.ceil(excelFilteredProducts.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return excelFilteredProducts.slice(start, start + pageSize);
  }, [excelFilteredProducts, currentPage, pageSize]);

  const getPanelTitle = () => {
    if (user?.department === 'Artwork') {
      if (activeTab === 'submit_pm_orders') return 'Submit PM Code Orders';
      if (activeTab === 'update_pm_codes') return 'Update PM Codes';
      return 'All Products';
    }
    return 'Products';
  };

  if (loading) {
    return <div className="loading">Loading products...</div>;
  }

  return (
    <div className="main-container">
      <Header user={user} onLogout={onLogout} />

      <div className="panel">
        <div className="panel-header" style={{ flexWrap: 'wrap', gap: '15px' }}>
          <h2>{getPanelTitle()}</h2>
          {/* Only show "Add Product" for Regulatory department */}
          {user?.department === 'Regulatory' && (
            <button className="submit-button" onClick={handleAddProductClick}>
              + Add Product
            </button>
          )}
        </div>

        {/* PM Code Filter Tabs - Only shown for Artwork department */}
        {user?.department === 'Artwork' && (
          <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center', background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <button
              type="button"
              onClick={() => handleTabChange('submit_pm_orders')}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                fontWeight: 600,
                fontSize: '14px',
                border: activeTab === 'submit_pm_orders' ? '2px solid #ea580c' : '1px solid #cbd5e1',
                backgroundColor: activeTab === 'submit_pm_orders' ? '#fff7ed' : '#ffffff',
                color: activeTab === 'submit_pm_orders' ? '#c2410c' : '#475569',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: activeTab === 'submit_pm_orders' ? '0 1px 3px rgba(234,88,12,0.2)' : 'none',
                transition: 'all 0.2s ease'
              }}
            >
              <span>Submit PM Code Orders</span>
              <span style={{
                background: activeTab === 'submit_pm_orders' ? '#ea580c' : '#e2e8f0',
                color: activeTab === 'submit_pm_orders' ? '#ffffff' : '#475569',
                borderRadius: '12px',
                padding: '2px 8px',
                fontSize: '12px'
              }}>
                {submitPmOrdersProducts.length}
              </span>
            </button>

            <button
              type="button"
              onClick={() => handleTabChange('update_pm_codes')}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                fontWeight: 600,
                fontSize: '14px',
                border: activeTab === 'update_pm_codes' ? '2px solid #0284c7' : '1px solid #cbd5e1',
                backgroundColor: activeTab === 'update_pm_codes' ? '#f0f9ff' : '#ffffff',
                color: activeTab === 'update_pm_codes' ? '#0369a1' : '#475569',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: activeTab === 'update_pm_codes' ? '0 1px 3px rgba(2,132,199,0.2)' : 'none',
                transition: 'all 0.2s ease'
              }}
            >
              <span>Update PM Codes</span>
              <span style={{
                background: activeTab === 'update_pm_codes' ? '#0284c7' : '#e2e8f0',
                color: activeTab === 'update_pm_codes' ? '#ffffff' : '#475569',
                borderRadius: '12px',
                padding: '2px 8px',
                fontSize: '12px'
              }}>
                {updatePmCodesProducts.length}
              </span>
            </button>

            <button
              type="button"
              onClick={() => handleTabChange('all')}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                fontWeight: 600,
                fontSize: '14px',
                border: activeTab === 'all' ? '2px solid #475569' : '1px solid #cbd5e1',
                backgroundColor: activeTab === 'all' ? '#f1f5f9' : '#ffffff',
                color: activeTab === 'all' ? '#0f172a' : '#475569',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s ease'
              }}
            >
              <span>All Products</span>
              <span style={{
                background: activeTab === 'all' ? '#475569' : '#e2e8f0',
                color: activeTab === 'all' ? '#ffffff' : '#475569',
                borderRadius: '12px',
                padding: '2px 8px',
                fontSize: '12px'
              }}>
                {products.length}
              </span>
            </button>
          </div>
        )}

        <ExcelActiveFiltersBar
          activeCount={activeFilterCount}
          isSorted={!!sortState.direction}
          onClearAll={clearAllFilters}
        />

        {/* Desktop Table View */}
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>
                  <ExcelHeaderCell
                    columnKey="sku_code"
                    label="SKU Code"
                    uniqueValues={uniqueValuesMap.sku_code}
                    selectedValues={filterState.sku_code}
                    sortDirection={sortState.columnKey === 'sku_code' ? sortState.direction : null}
                    onFilterChange={(sel) => setColumnFilter('sku_code', sel)}
                    onSortChange={(dir) => handleSort('sku_code', dir)}
                    onClearFilter={() => clearColumnFilter('sku_code')}
                  />
                </th>
                <th>
                  <ExcelHeaderCell
                    columnKey="product_name"
                    label="Product Name"
                    uniqueValues={uniqueValuesMap.product_name}
                    selectedValues={filterState.product_name}
                    sortDirection={sortState.columnKey === 'product_name' ? sortState.direction : null}
                    onFilterChange={(sel) => setColumnFilter('product_name', sel)}
                    onSortChange={(dir) => handleSort('product_name', dir)}
                    onClearFilter={() => clearColumnFilter('product_name')}
                  />
                </th>
                <th>
                  <ExcelHeaderCell
                    columnKey="country"
                    label="Country"
                    uniqueValues={uniqueValuesMap.country}
                    selectedValues={filterState.country}
                    sortDirection={sortState.columnKey === 'country' ? sortState.direction : null}
                    onFilterChange={(sel) => setColumnFilter('country', sel)}
                    onSortChange={(dir) => handleSort('country', dir)}
                    onClearFilter={() => clearColumnFilter('country')}
                  />
                </th>
                <th>
                  <ExcelHeaderCell
                    columnKey="customer"
                    label="Customer"
                    uniqueValues={uniqueValuesMap.customer}
                    selectedValues={filterState.customer}
                    sortDirection={sortState.columnKey === 'customer' ? sortState.direction : null}
                    onFilterChange={(sel) => setColumnFilter('customer', sel)}
                    onSortChange={(dir) => handleSort('customer', dir)}
                    onClearFilter={() => clearColumnFilter('customer')}
                  />
                </th>
                <th>
                  <ExcelHeaderCell
                    columnKey="pack_size"
                    label="Pack Size"
                    uniqueValues={uniqueValuesMap.pack_size}
                    selectedValues={filterState.pack_size}
                    sortDirection={sortState.columnKey === 'pack_size' ? sortState.direction : null}
                    onFilterChange={(sel) => setColumnFilter('pack_size', sel)}
                    onSortChange={(dir) => handleSort('pack_size', dir)}
                    onClearFilter={() => clearColumnFilter('pack_size')}
                  />
                </th>
                <th>
                  <ExcelHeaderCell
                    columnKey="pm_code"
                    label="PM Code"
                    uniqueValues={uniqueValuesMap.pm_code}
                    selectedValues={filterState.pm_code}
                    sortDirection={sortState.columnKey === 'pm_code' ? sortState.direction : null}
                    onFilterChange={(sel) => setColumnFilter('pm_code', sel)}
                    onSortChange={(dir) => handleSort('pm_code', dir)}
                    onClearFilter={() => clearColumnFilter('pm_code')}
                  />
                </th>
                <th>
                  <ExcelHeaderCell
                    columnKey="artwork_status"
                    label="Artwork Status"
                    uniqueValues={uniqueValuesMap.artwork_status}
                    selectedValues={filterState.artwork_status}
                    sortDirection={sortState.columnKey === 'artwork_status' ? sortState.direction : null}
                    onFilterChange={(sel) => setColumnFilter('artwork_status', sel)}
                    onSortChange={(dir) => handleSort('artwork_status', dir)}
                    onClearFilter={() => clearColumnFilter('artwork_status')}
                  />
                </th>
                <th>
                  <ExcelHeaderCell
                    columnKey="pm_request_status"
                    label="PM Request Status"
                    uniqueValues={uniqueValuesMap.pm_request_status}
                    selectedValues={filterState.pm_request_status}
                    sortDirection={sortState.columnKey === 'pm_request_status' ? sortState.direction : null}
                    onFilterChange={(sel) => setColumnFilter('pm_request_status', sel)}
                    onSortChange={(dir) => handleSort('pm_request_status', dir)}
                    onClearFilter={() => clearColumnFilter('pm_request_status')}
                  />
                </th>
                <th>History</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {excelFilteredProducts.length === 0 ? (
                <tr>
                  <td colSpan={10} style={{ textAlign: 'center', padding: '35px', color: '#64748b', fontSize: '15px' }}>
                    {activeTab === 'submit_pm_orders'
                      ? 'No PM Code Orders currently awaiting Artwork submission.'
                      : activeTab === 'update_pm_codes'
                      ? 'No products available for PM Code updates.'
                      : 'No products found matching the criteria.'}
                  </td>
                </tr>
              ) : (
                paginatedProducts.map((product) => {
                const requests = product.pm_code_requests || [];
                const latestRequest = requests[requests.length - 1];
                const showGetPmCode = isRegulatory && (product.artwork_status !== 'Available') && (!product.primary_pm_code) && (!latestRequest || latestRequest.status === 'APPROVED');

                const getPmRequestStatusText = () => {
                  if (!latestRequest) return '—';
                  switch (latestRequest.status) {
                    case 'PENDING_ARTWORK': return 'Awaiting Artwork PM Code';
                    case 'AWAITING_REGULATORY_APPROVAL': return 'Awaiting Regulatory Approval';
                    case 'APPROVED': return 'Approved';
                    case 'REJECTED': return 'Rejected';
                    default: return latestRequest.status;
                  }
                };

                const getPmRequestStatusClass = () => {
                  if (!latestRequest) return '';
                  switch (latestRequest.status) {
                    case 'PENDING_ARTWORK': return 'status-hold';
                    case 'AWAITING_REGULATORY_APPROVAL': return 'status-new';
                    case 'APPROVED': return 'status-accepted';
                    case 'REJECTED': return 'status-risk';
                    default: return '';
                  }
                };

                return (
                  <tr key={product.id} onClick={() => navigate(`/products/${product.id}`)} style={{ cursor: 'pointer' }}>
                    <td><strong style={{ color: '#0284c7' }}>{product.sku_code}</strong></td>
                    <td><strong>{product.product_name}</strong></td>
                    <td>{product.country?.name || "-"}</td>
                    <td>{product.customer}</td>
                    <td>{product.pack_size}</td>
                    {/* <td>{product.standard_batch_size}</td>
                    <td>{product.moq}</td> */}
                    <td>{product.primary_pm_code || latestRequest?.current_primary_pm_code || '—'}</td>
                    <td>
                      <span className={`status-badge ${getArtworkStatusClass(product.artwork_status)}`}>
                        {product.artwork_status}
                      </span>
                    </td>
                    <td>
                      {latestRequest ? (
                        <span className={`status-badge ${getPmRequestStatusClass()}`}>
                          {getPmRequestStatusText()}
                        </span>
                      ) : '—'}
                    </td>
                    <td>
                      <button
                        className="nav-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setHistoryModal(product);
                        }}
                      >
                        Show History
                      </button>
                    </td>
                    <td>
                      {showGetPmCode && (
                        <button
                          className="submit-button"
                          onClick={() => handleRequestPmCode(product.sku_code)}
                        >
                          Get PM Code
                        </button>
                      )}
                      {isRegulatory && latestRequest && latestRequest.status === 'PENDING_ARTWORK' && (
                        <span style={{ color: '#666', fontSize: '0.9em' }}>Awaiting Artwork</span>
                      )}
                      {isRegulatory && latestRequest && latestRequest.status === 'AWAITING_REGULATORY_APPROVAL' && (
                        <div style={{ display: 'flex', gap: '5px' }}>
                          <button
                            className="submit-button"
                            style={{ background: '#4caf50', borderColor: '#4caf50', padding: '4px 8px', fontSize: '0.85em' }}
                            onClick={() => setAcceptModal({
                              requestId: latestRequest.id,
                              primaryPmCode: latestRequest.current_primary_pm_code || latestRequest.current_pm_code || '',
                              secondaryPmCode: latestRequest.current_secondary_pm_code || '',
                              leafPmCode: latestRequest.current_leaf_pm_code || '',
                              artworkStatus: product.artwork_status === 'Available' ? 'Available' : 'Available'
                            })}
                          >
                            Accept ({latestRequest.current_primary_pm_code || latestRequest.current_pm_code})
                          </button>
                          <button
                            className="nav-button"
                            style={{ background: '#f44336', borderColor: '#f44336', color: '#fff', padding: '4px 8px', fontSize: '0.85em' }}
                            onClick={() => setRejectModal({ requestId: latestRequest.id })}
                          >
                            Reject
                          </button>
                        </div>
                      )}
                      {user?.department === 'Artwork' && latestRequest && latestRequest.status === 'AWAITING_REGULATORY_APPROVAL' && (
                        <span style={{ color: '#0284c7', fontSize: '0.85em', fontWeight: 600 }}>Awaiting Regulatory Approval</span>
                      )}
                      {user?.department === 'Artwork' && latestRequest && latestRequest.status === 'PENDING_ARTWORK' && (
                        <button
                          className="submit-button"
                          style={{ background: '#ff9800', borderColor: '#ff9800' }}
                          onClick={() => {
                            setArtworkSubmitModal({ requestId: latestRequest.id, sku: product.sku_code });
                            setPrimaryPmCodeInput(latestRequest.current_primary_pm_code || product.primary_pm_code || '');
                            setSecondaryPmCodeInput(latestRequest.current_secondary_pm_code || product.secondary_pm_code || '');
                            setLeafPmCodeInput(latestRequest.current_leaf_pm_code || product.leaf_pm_code || '');
                          }}
                        >
                          Submit PM Code
                        </button>
                      )}
                      {user?.department === 'Artwork' && (!latestRequest || (latestRequest.status !== 'PENDING_ARTWORK' && latestRequest.status !== 'AWAITING_REGULATORY_APPROVAL')) && (
                        <button
                          className="submit-button"
                          style={{ background: '#ff9800', borderColor: '#ff9800' }}
                          onClick={() => {
                            setPmModal({ sku: String(product.sku_code), primaryPmCode: String(product.primary_pm_code || ''), secondaryPmCode: String(product.secondary_pm_code || ''), leafPmCode: String(product.leaf_pm_code || '') });
                            setPrimaryPmCodeInput(product.primary_pm_code || latestRequest?.current_primary_pm_code || '');
                            setSecondaryPmCodeInput(product.secondary_pm_code || latestRequest?.current_secondary_pm_code || '');
                            setLeafPmCodeInput(product.leaf_pm_code || latestRequest?.current_leaf_pm_code || '');
                          }}
                        >
                          Update PM Code
                        </button>
                      )}
                      {!showGetPmCode &&
                        !(isRegulatory && latestRequest && (latestRequest.status === 'PENDING_ARTWORK' || latestRequest.status === 'AWAITING_REGULATORY_APPROVAL')) &&
                        user?.department !== 'Artwork' && (
                          <span style={{ color: '#999' }}>&lt; 1 day</span>
                        )}
                    </td>
                  </tr>
                );
              })
            )}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="mobile-table-cards">
          {paginatedProducts.map((product) => {
            const requests = product.pm_code_requests || [];
            const latestRequest = requests[requests.length - 1];
            const showGetPmCode = isRegulatory && (product.artwork_status !== 'Available') && (!product.primary_pm_code) && (!latestRequest || latestRequest.status === 'APPROVED');

            const getPmRequestStatusText = () => {
              if (!latestRequest) return '—';
              switch (latestRequest.status) {
                case 'PENDING_ARTWORK': return 'Awaiting Artwork PM Code';
                case 'AWAITING_REGULATORY_APPROVAL': return 'Awaiting Regulatory Approval';
                case 'APPROVED': return 'Approved';
                case 'REJECTED': return 'Rejected';
                default: return latestRequest.status;
              }
            };

            const getPmRequestStatusClass = () => {
              if (!latestRequest) return '';
              switch (latestRequest.status) {
                case 'PENDING_ARTWORK': return 'status-hold';
                case 'AWAITING_REGULATORY_APPROVAL': return 'status-new';
                case 'APPROVED': return 'status-accepted';
                case 'REJECTED': return 'status-risk';
                default: return '';
              }
            };

            return (
              <div key={product.id} className="mobile-card" onClick={() => navigate(`/products/${product.id}`)} style={{ cursor: 'pointer' }}>
                <div className="mobile-card-row">
                  <span className="mobile-card-label">SKU Code</span>
                  <span className="mobile-card-value">{product.sku_code}</span>
                </div>
                <div className="mobile-card-row">
                  <span className="mobile-card-label">Product Name</span>
                  <span className="mobile-card-value">{product.product_name}</span>
                </div>
                <div className="mobile-card-row">
                  <span className="mobile-card-label">Country</span>
                  <span className="mobile-card-value">{product.country?.name || "-"}</span>
                </div>
                <div className="mobile-card-row">
                  <span className="mobile-card-label">Customer</span>
                  <span className="mobile-card-value">{product.customer}</span>
                </div>
                <div className="mobile-card-row">
                  <span className="mobile-card-label">Pack Size</span>
                  <span className="mobile-card-value">{product.pack_size}</span>
                </div>
                <div className="mobile-card-row">
                  <span className="mobile-card-label">Batch Size</span>
                  <span className="mobile-card-value">{product.standard_batch_size}</span>
                </div>
                <div className="mobile-card-row">
                  <span className="mobile-card-label">MOQ</span>
                  <span className="mobile-card-value">{product.moq}</span>
                </div>
                <div className="mobile-card-row">
                  <span className="mobile-card-label">PM Code</span>
                  <span className="mobile-card-value">{product.primary_pm_code || latestRequest?.current_primary_pm_code || '—'}</span>
                </div>
                <div className="mobile-card-row">
                  <span className="mobile-card-label">Artwork Status</span>
                  <span className="mobile-card-value">
                    <span className={`status-badge ${getArtworkStatusClass(product.artwork_status)}`}>
                      {product.artwork_status}
                    </span>
                  </span>
                </div>
                <div className="mobile-card-row">
                  <span className="mobile-card-label">PM Request Status</span>
                  <span className="mobile-card-value">
                    {latestRequest ? (
                      <span className={`status-badge ${getPmRequestStatusClass()}`}>
                        {getPmRequestStatusText()}
                      </span>
                    ) : '—'}
                  </span>
                </div>
                <div className="mobile-card-row" style={{ marginTop: '8px' }}>
                  <button
                    className="nav-button"
                    style={{ width: '100%' }}
                    onClick={() => setHistoryModal(product)}
                  >
                    Show History
                  </button>
                </div>
                <div className="mobile-card-row" style={{ marginTop: '8px' }}>
                  {showGetPmCode && (
                    <button
                      className="submit-button"
                      style={{ width: '100%' }}
                      onClick={() => handleRequestPmCode(product.sku_code)}
                    >
                      Get PM Code
                    </button>
                  )}
                  {isRegulatory && latestRequest && latestRequest.status === 'PENDING_ARTWORK' && (
                    <span style={{ color: '#666', fontSize: '0.9em', textAlign: 'center', width: '100%' }}>Awaiting Artwork</span>
                  )}
                  {isRegulatory && latestRequest && latestRequest.status === 'AWAITING_REGULATORY_APPROVAL' && (
                    <div style={{ display: 'flex', gap: '5px', width: '100%' }}>
                      <button
                        className="submit-button"
                        style={{ background: '#4caf50', borderColor: '#4caf50', flex: 1, padding: '6px', fontSize: '0.85em' }}
                        onClick={() => setAcceptModal({
                          requestId: latestRequest.id,
                          primaryPmCode: latestRequest.current_primary_pm_code || latestRequest.current_pm_code || '',
                          secondaryPmCode: latestRequest.current_secondary_pm_code || '',
                          leafPmCode: latestRequest.current_leaf_pm_code || '',
                          artworkStatus: product.artwork_status === 'Available' ? 'Available' : 'Available'
                        })}
                      >
                        Accept ({latestRequest.current_primary_pm_code || latestRequest.current_pm_code})
                      </button>
                      <button
                        className="nav-button"
                        style={{ background: '#f44336', borderColor: '#f44336', color: '#fff', flex: 1, padding: '6px', fontSize: '0.85em' }}
                        onClick={() => setRejectModal({
                          requestId: latestRequest.id,
                          sku: product.sku_code,
                          primaryPmCode: latestRequest.current_primary_pm_code || latestRequest.current_pm_code || '—',
                          secondaryPmCode: latestRequest.current_secondary_pm_code || '—',
                          leafPmCode: latestRequest.current_leaf_pm_code || '—'
                        })}
                      >
                        Reject
                      </button>
                    </div>
                  )}
                  {user?.department === 'Artwork' && latestRequest && latestRequest.status === 'AWAITING_REGULATORY_APPROVAL' && (
                    <span style={{ color: '#0284c7', fontSize: '0.85em', fontWeight: 600, textAlign: 'center', width: '100%' }}>Awaiting Regulatory Approval</span>
                  )}
                  {user?.department === 'Artwork' && latestRequest && latestRequest.status === 'PENDING_ARTWORK' && (
                    <button
                      className="submit-button"
                      style={{ background: '#ff9800', borderColor: '#ff9800', width: '100%' }}
                      onClick={() => {
                        setArtworkSubmitModal({ requestId: latestRequest.id, sku: product.sku_code });
                        setPrimaryPmCodeInput(latestRequest.current_primary_pm_code || product.primary_pm_code || '');
                        setSecondaryPmCodeInput(latestRequest.current_secondary_pm_code || product.secondary_pm_code || '');
                        setLeafPmCodeInput(latestRequest.current_leaf_pm_code || product.leaf_pm_code || '');
                      }}
                    >
                      Submit PM Code
                    </button>
                  )}
                  {user?.department === 'Artwork' && (!latestRequest || (latestRequest.status !== 'PENDING_ARTWORK' && latestRequest.status !== 'AWAITING_REGULATORY_APPROVAL')) && (
                    <button
                      className="submit-button"
                      style={{ background: '#ff9800', borderColor: '#ff9800', width: '100%' }}
                      onClick={() => {
                        setPmModal({ sku: String(product.sku_code), primaryPmCode: String(product.primary_pm_code || ''), secondaryPmCode: String(product.secondary_pm_code || ''), leafPmCode: String(product.leaf_pm_code || '') });
                        setPrimaryPmCodeInput(product.primary_pm_code || latestRequest?.current_primary_pm_code || '');
                        setSecondaryPmCodeInput(product.secondary_pm_code || latestRequest?.current_secondary_pm_code || '');
                        setLeafPmCodeInput(product.leaf_pm_code || latestRequest?.current_leaf_pm_code || '');
                      }}
                    >
                      Update PM Code
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {products.length === 0 && (
          <p style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            No products found
          </p>
        )}

        {/* Pagination Controls */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', padding: '12px 16px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
          <button
            className="nav-button"
            disabled={currentPage === 1 || loading}
            onClick={() => setPage(prev => Math.max(1, prev - 1))}
            style={{ opacity: (currentPage === 1 || loading) ? 0.5 : 1, cursor: (currentPage === 1 || loading) ? 'not-allowed' : 'pointer' }}
          >
            ← Previous
          </button>
          <span style={{ fontWeight: 600, fontSize: '0.9rem', color: '#475569' }}>
            Page {currentPage} of {totalPages} ({excelFilteredProducts.length} {excelFilteredProducts.length === 1 ? 'product' : 'products'})
          </span>
          <button
            className="nav-button"
            disabled={currentPage >= totalPages || loading}
            onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
            style={{ opacity: (currentPage >= totalPages || loading) ? 0.5 : 1, cursor: (currentPage >= totalPages || loading) ? 'not-allowed' : 'pointer' }}
          >
            Next →
          </button>
        </div>
      </div>


      {/* Add Product Modal — for Regulatory */}
      {showModal && isRegulatory && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{isEditMode ? 'Edit Product' : 'Add New Product'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group" style={{ position: 'relative' }}>
                <label>Product Name *</label>
                <input
                  type="text"
                  value={formData.product_name}
                  onChange={handleProductNameChange}
                  onFocus={() => { if (skuSuggestions.length > 0) setShowSuggestions(true); }}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  required
                  placeholder="e.g. Paracetamol 500mg"
                />
                {showSuggestions && skuSuggestions.length > 0 && (
                  <ul className="suggestions-list" style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #ccc', zIndex: 10, listStyle: 'none', padding: 0, margin: 0, maxHeight: '200px', overflowY: 'auto' }}>
                    {skuSuggestions.map(s => (
                      <li
                        key={s.sku_code}
                        style={{ padding: '8px', cursor: 'pointer', borderBottom: '1px solid #eee' }}
                        onMouseDown={() => handleProductSelect(s)}
                      >
                        {s.product_name} ({s.sku_code})
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="form-group">
                <label>SKU Code * {isEditMode && <span style={{ fontSize: '0.8em', color: '#64748b' }}>(Locked in Edit Mode)</span>}</label>
                <input
                  type="text"
                  value={formData.sku_code}
                  onChange={(e) => setFormData({ ...formData, sku_code: e.target.value })}
                  required
                  disabled={isEditMode}
                  style={isEditMode ? { background: '#f1f5f9', cursor: 'not-allowed', opacity: 0.8 } : {}}
                  placeholder="e.g. SKU-101"
                />
              </div>

              <div className="form-group">
                <label>Manufacturing Unit *</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                >
                  <option value="PP">PP </option>
                  <option value="PNS">PNS</option>
                </select>
              </div>

              <div className="form-group">
                <label>Country *</label>
                <select
                  value={formData.country_id !== null ? String(formData.country_id) : ""}
                  onChange={(e) => setFormData({ ...formData, country_id: parseInt(e.target.value) })}

                  required
                >
                  <option value="">— Select Country —</option>
                  {countries.map((country) => (
                    <option key={country.id} value={country.id}>{country.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Customer *</label>
                <select
                  value={formData.customer}
                  onChange={(e) => setFormData({ ...formData, customer: e.target.value })}
                  required
                  disabled={!formData.country_id}
                >
                  <option value="">{formData.country_id ? "— Select Customer —" : "Select Country First"}</option>
                  {filteredCustomers.map((c) => (
                    <option key={c.id} value={c.customer_name}>{c.customer_name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Pack Size</label>
                <input
                  type="text"
                  value={formData.pack_size}
                  onChange={(e) => setFormData({ ...formData, pack_size: e.target.value })}
                  placeholder="e.g. 10x10 Strips"
                />
              </div>

              <div className="form-group">
                <label>Standard Batch Size</label>
                <input
                  type="number"
                  value={formData.standard_batch_size}
                  onChange={(e) => setFormData({ ...formData, standard_batch_size: e.target.value })}
                  placeholder="e.g. 100000"
                />
              </div>

              <div className="form-group">
                <label>MOQ</label>
                <input
                  type="number"
                  value={formData.moq}
                  onChange={(e) => setFormData({ ...formData, moq: e.target.value })}
                  placeholder="e.g. 10000"
                />
              </div>

              <div className="form-group">
                <label>Artwork Status</label>
                <select
                  value={formData.artwork_status}
                  onChange={(e) => setFormData({ ...formData, artwork_status: e.target.value, primary_pm_code: '' })}
                >
                  <option value="Available">Available</option>
                  <option value="Pending">Pending</option>
                  <option value="Not Available">Not Available</option>
                </select>
              </div>

              {/* PM Code — editable if Artwork Status = Available */}
              {formData.artwork_status === 'Available' ? (
                <>
                  <div className="form-group">
                    <label>Primary PM Code *</label>
                    <input
                      type="text"
                      value={formData.primary_pm_code}
                      onChange={(e) => setFormData({ ...formData, primary_pm_code: e.target.value })}
                      required
                      placeholder="e.g. PM-PARA-500"
                    />
                  </div>
                  <div className="form-group">
                    <label>Secondary PM Code</label>
                    <input
                      type="text"
                      value={formData.secondary_pm_code}
                      onChange={(e) => setFormData({ ...formData, secondary_pm_code: e.target.value })}
                      placeholder="e.g. PM-PARA-500-SEC"
                    />
                  </div>
                  <div className="form-group">
                    <label>Leaf PM Code</label>
                    <input
                      type="text"
                      value={formData.leaf_pm_code}
                      onChange={(e) => setFormData({ ...formData, leaf_pm_code: e.target.value })}
                      placeholder="e.g. PM-PARA-500-LEAF"
                    />
                  </div>
                </>
              ) : (
                <div className="form-group">
                  <label>PM Code</label>
                  <input
                    type="text"
                    value="To be updated by Artwork via Get PM Code workflow"
                    disabled
                    style={{ color: '#999', background: '#f5f5f5' }}
                  />
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button type="submit" className="submit-button">
                  {isEditMode ? 'Save Product' : 'Add Product'}
                </button>
                <button type="button" className="nav-button" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* PM Code Update Modal for Regulatory */}
      {pmModal && (
        <div className="modal-overlay" onClick={() => setPmModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Update PM Code — {pmModal.sku}</h2>
            <div className="form-group">
              <label>PM Code *</label>
              <input
                type="text"
                value={primaryPmCodeInput}
                onChange={(e) => setPrimaryPmCodeInput(e.target.value)}
                placeholder="Enter PM Code"
                required
                style={{ marginBottom: '15px' }}
              />
            </div>
            <div className="form-group">
              <label>Secondary PM Code (Optional)</label>
              <input
                type="text"
                value={secondaryPmCodeInput}
                onChange={(e) => setSecondaryPmCodeInput(e.target.value)}
                placeholder="Enter Secondary PM Code"
                style={{ marginBottom: '15px' }}
              />
            </div>
            <div className="form-group">
              <label>Leaflet / Leaf PM Code (Optional)</label>
              <input
                type="text"
                value={leafPmCodeInput}
                onChange={(e) => setLeafPmCodeInput(e.target.value)}
                placeholder="Enter Leaf PM Code"
                style={{ marginBottom: '15px' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button className="submit-button" onClick={handlePmCodeUpdate} disabled={!primaryPmCodeInput.trim()}>
                Save PM Code
              </button>
              <button className="nav-button" onClick={() => setPmModal(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Accept PM Code & Update Artwork Status Modal for Regulatory */}
      {acceptModal && (
        <div className="modal-overlay" onClick={() => { setAcceptModal(null); setAcceptRemarks(''); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '520px' }}>
            <h2 style={{ margin: '0 0 12px 0', color: '#1a237e' }}>Accept PM Code & Update Artwork</h2>

            <div style={{
              background: '#e8f5e9',
              border: '1px solid #4caf50',
              borderRadius: '8px',
              padding: '14px 18px',
              marginBottom: '16px',
              fontSize: '0.95em',
              color: '#2e7d32'
            }}>
              <p style={{ margin: '0 0 10px 0', fontWeight: 'bold' }}>Update PM Codes and Artwork Status before approving:</p>

              <div className="form-group" style={{ marginBottom: '10px' }}>
                <label style={{ fontWeight: 600, color: '#1b5e20' }}>Artwork Status *</label>
                <select
                  value={acceptModal.artworkStatus}
                  onChange={(e) => setAcceptModal({ ...acceptModal, artworkStatus: e.target.value })}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #a5d6a7' }}
                >
                  <option value="Available">Available</option>
                  <option value="Pending">Pending</option>
                  <option value="Not Available">Not Available</option>
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: '10px' }}>
                <label style={{ fontWeight: 600, color: '#1b5e20' }}>Primary PM Code *</label>
                <input
                  type="text"
                  value={acceptModal.primaryPmCode}
                  onChange={(e) => setAcceptModal({ ...acceptModal, primaryPmCode: e.target.value })}
                  placeholder="Primary PM Code"
                  required
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #a5d6a7', boxSizing: 'border-box' }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: '10px' }}>
                <label style={{ fontWeight: 600, color: '#1b5e20' }}>Secondary PM Code</label>
                <input
                  type="text"
                  value={acceptModal.secondaryPmCode}
                  onChange={(e) => setAcceptModal({ ...acceptModal, secondaryPmCode: e.target.value })}
                  placeholder="Secondary PM Code"
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #a5d6a7', boxSizing: 'border-box' }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: '0' }}>
                <label style={{ fontWeight: 600, color: '#1b5e20' }}>Leaf PM Code</label>
                <input
                  type="text"
                  value={acceptModal.leafPmCode}
                  onChange={(e) => setAcceptModal({ ...acceptModal, leafPmCode: e.target.value })}
                  placeholder="Leaf PM Code"
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #a5d6a7', boxSizing: 'border-box' }}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Approval Remarks (Optional)</label>
              <textarea
                value={acceptRemarks}
                onChange={(e) => setAcceptRemarks(e.target.value)}
                placeholder="Enter approval remarks..."
                rows={2}
                style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: '6px', padding: '8px' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
              <button
                className="submit-button"
                style={{ background: '#4caf50', borderColor: '#4caf50' }}
                disabled={!acceptModal.primaryPmCode.trim()}
                onClick={() => {
                  handleDecidePmCode(
                    acceptModal.requestId,
                    'ACCEPT',
                    acceptRemarks,
                    acceptModal.primaryPmCode,
                    acceptModal.secondaryPmCode,
                    acceptModal.leafPmCode,
                    acceptModal.artworkStatus
                  );
                  setAcceptModal(null);
                  setAcceptRemarks('');
                }}
              >
                Confirm & Accept
              </button>
              <button className="nav-button" onClick={() => { setAcceptModal(null); setAcceptRemarks(''); }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject PM Code Modal for Regulatory */}
      {rejectModal && (
        <div className="modal-overlay" onClick={() => { setRejectModal(null); setRejectRemarks(''); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Reject PM Code Request</h2>
            {rejectModal.sku && (
              <div style={{
                background: '#f8fafc',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                padding: '12px 16px',
                marginBottom: '14px',
                fontSize: '0.9em',
                color: '#334155'
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: '6px', color: '#0f172a' }}>
                  SKU: <span style={{ color: '#0284c7' }}>{rejectModal.sku}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', fontSize: '0.85em' }}>
                  <div><strong>Primary PM Code:</strong> {rejectModal.primaryPmCode || '—'}</div>
                  <div><strong>Secondary PM Code:</strong> {rejectModal.secondaryPmCode || '—'}</div>
                  <div><strong>Leaflet PM Code:</strong> {rejectModal.leafPmCode || '—'}</div>
                </div>
              </div>
            )}
            <div style={{
              background: '#ffebee',
              border: '1px solid #f44336',
              borderRadius: '6px',
              padding: '10px 14px',
              marginBottom: '14px',
              fontSize: '0.9em',
              color: '#c62828'
            }}>
              ⚠️ You are rejecting the PM Code submitted by the Artwork team. Please specify remarks/reasons for rejection. <strong>Remarks are mandatory.</strong>
            </div>
            <div className="form-group">
              <label>Remarks *</label>
              <textarea
                value={rejectRemarks}
                onChange={(e) => setRejectRemarks(e.target.value)}
                placeholder="Enter rejection reasons..."
                rows={4}
                required
                style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: '4px', padding: '8px' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button
                className="submit-button"
                style={{ background: '#f44336', borderColor: '#f44336' }}
                onClick={() => {
                  if (!rejectRemarks.trim()) {
                    alert('Remarks are required for rejection');
                    return;
                  }
                  handleDecidePmCode(rejectModal.requestId, 'REJECT', rejectRemarks);
                  setRejectModal(null);
                  setRejectRemarks('');
                }}
                disabled={!rejectRemarks.trim()}
              >
                Reject PM Code
              </button>
              <button className="nav-button" onClick={() => { setRejectModal(null); setRejectRemarks(''); }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Submit PM Code Modal for Artwork */}
      {artworkSubmitModal && (
        <div className="modal-overlay" onClick={() => { setArtworkSubmitModal(null); setPrimaryPmCodeInput(''); setSecondaryPmCodeInput(''); setLeafPmCodeInput(''); setArtworkRemarks(''); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Submit PM Codes for SKU: {artworkSubmitModal.sku}</h2>
            <div className="form-group">
              <label>Primary PM Code *</label>
              <input
                type="text"
                value={primaryPmCodeInput}
                onChange={(e) => setPrimaryPmCodeInput(e.target.value)}
                placeholder="Enter Primary PM Code (e.g. PM-PARA-500)"
                required
              />
            </div>
            <div className="form-group">
              <label>Secondary PM Code (Optional)</label>
              <input
                type="text"
                value={secondaryPmCodeInput}
                onChange={(e) => setSecondaryPmCodeInput(e.target.value)}
                placeholder="Enter Secondary PM Code"
                style={{ marginBottom: '15px' }}
              />
            </div>
            <div className="form-group">
              <label>Leaflet / Leaf PM Code (Optional)</label>
              <input
                type="text"
                value={leafPmCodeInput}
                onChange={(e) => setLeafPmCodeInput(e.target.value)}
                placeholder="Enter Leaf PM Code"
                style={{ marginBottom: '15px' }}
              />
            </div>
            <div className="form-group">
              <label>Remarks (Optional)</label>
              <textarea
                value={artworkRemarks}
                onChange={(e) => setArtworkRemarks(e.target.value)}
                placeholder="Enter any additional details or remarks..."
                rows={3}
                style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: '4px', padding: '8px' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button
                className="submit-button"
                onClick={handleArtworkSubmit}
                disabled={!primaryPmCodeInput.trim()}
              >
                Submit to Regulatory
              </button>
              <button className="nav-button" onClick={() => { setArtworkSubmitModal(null); setPrimaryPmCodeInput(''); setSecondaryPmCodeInput(''); setLeafPmCodeInput(''); setArtworkRemarks(''); }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {historyModal && (
        <div className="modal-overlay" onClick={() => setHistoryModal(null)}>
          <div className="modal" style={{ maxWidth: '800px', width: '90%' }} onClick={(e) => e.stopPropagation()}>
            <h2>PM Code Request History — SKU: {historyModal.sku_code}</h2>
            <div className="table-container" style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Dept</th>
                    <th>Action</th>
                    <th>PM Code</th>
                    <th>Remarks</th>
                    <th>Response Time</th>
                  </tr>
                </thead>
                <tbody>
                  {(historyModal.pm_code_requests?.flatMap((req: any) => req.transactions || []) || [])
                    .sort((a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
                    .map((tx: any) => (
                      <tr key={tx.id}>
                        <td>{formatDateTime(tx.created_at)}</td>
                        <td>
                          <span className={`status-badge ${tx.action_by_dept === 'Regulatory' ? 'status-new' : 'status-execution'}`}>
                            {tx.action_by_dept}
                          </span>
                        </td>
                        <td>
                          {tx.from_state ? (
                            <span>{tx.from_state} ➔ {tx.to_state}</span>
                          ) : (
                            <span>Created ➔ {tx.to_state}</span>
                          )}
                        </td>
                        <td>{tx.primary_pm_code || tx.pm_code || '—'}</td>
                        <td>{tx.remarks || '—'}</td>
                        <td>
                          {tx.response_time_days > 0 ? (
                            <span style={{ fontWeight: 'bold', color: '#e65100' }}>
                              {tx.response_time_days} day(s)
                            </span>
                          ) : (
                            <span style={{ color: '#999' }}>&lt; 1 day</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  {(!historyModal.pm_code_requests || historyModal.pm_code_requests.length === 0 ||
                    historyModal.pm_code_requests.flatMap((req: any) => req.transactions || []).length === 0) && (
                      <tr>
                        <td colSpan={6} style={{ textAlign: 'center', padding: '20px', color: '#888' }}>
                          No PM Code request history records found for this product.
                        </td>
                      </tr>
                    )}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button className="submit-button" onClick={() => setHistoryModal(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Full Product Details Modal */}
      {viewProductModal && (
        <div className="modal-overlay" onClick={() => setViewProductModal(null)}>
          <div className="modal" style={{ maxWidth: '650px', width: '90%' }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ color: '#1a237e', marginBottom: '16px' }}>
              📦 Product Details — {viewProductModal.product_name}
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', background: '#f8fafc', padding: '20px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <div><strong>SKU Code:</strong> {viewProductModal.sku_code}</div>
              <div><strong>Product Name:</strong> {viewProductModal.product_name}</div>
              <div><strong>Country:</strong> {viewProductModal.country?.name || '—'}</div>
              <div><strong>Customer:</strong> {viewProductModal.customer || '—'}</div>
              <div><strong>Pack Size:</strong> {viewProductModal.pack_size || '—'}</div>
              <div><strong>Standard Batch Size:</strong> {viewProductModal.standard_batch_size || '—'}</div>
              <div><strong>MOQ:</strong> {viewProductModal.moq || '—'}</div>
              <div><strong>Primary PM Code:</strong> {viewProductModal.primary_pm_code || '—'}</div>
              <div><strong>Secondary PM Code:</strong> {viewProductModal.secondary_pm_code || '—'}</div>
              <div><strong>Leaf PM Code:</strong> {viewProductModal.leaf_pm_code || '—'}</div>
              <div style={{ gridColumn: 'span 2' }}>
                <strong>Artwork Status:</strong>{' '}
                <span className={`status-badge ${getArtworkStatusClass(viewProductModal.artwork_status)}`}>
                  {viewProductModal.artwork_status}
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button className="submit-button" onClick={() => setViewProductModal(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
